import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { Store } from "../../../../api";

import styles from "./StoreCatalogPage.module.scss";
import {
  SORT_OPTIONS,
  getCategoriesResponse,
  getFiltersResponse,
  getImageUrl,
  getProductColors,
  getProductsResponse,
  toggleValue,
} from "./StoreCatalogPage.form";

const LIMIT = 12;
const MAX_VISIBLE_COLORS = 5;
const COLOR_FILTER_VISIBLE_LIMIT = 20;
const SIZE_FILTER_VISIBLE_LIMIT = 12;
const SIZE_SEARCH_THRESHOLD = 12;

function FilterSection({ title, count, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className={styles.FilterGroup}>
      <button
        type="button"
        className={styles.FilterGroupHeader}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className={styles.FilterGroupTitle}>
          {title}

          {count > 0 && (
            <span className={styles.FilterGroupCount}>{count}</span>
          )}
        </span>

        <span
          className={styles.FilterGroupChevron}
          data-open={open}
          aria-hidden="true"
        >
          ⌄
        </span>
      </button>

      {open && <div className={styles.FilterGroupBody}>{children}</div>}
    </div>
  );
}

export function StoreCatalogPage() {
  const { categorySlug } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [categories, setCategories] = useState([]);
  const [filterBounds, setFilterBounds] = useState({
    colors: [],
    sizes: [],
  });

  const [selectedColors, setSelectedColors] = useState(() =>
    (searchParams.get("color") || "").split(",").filter(Boolean)
  );
  const [selectedSizes, setSelectedSizes] = useState(() =>
    (searchParams.get("size") || "").split(",").filter(Boolean)
  );
  const [showAllColors, setShowAllColors] = useState(false);
  const [sizeQuery, setSizeQuery] = useState("");
  const [showAllSizes, setShowAllSizes] = useState(false);
  const [showOutOfStock, setShowOutOfStock] = useState(
    () => searchParams.get("outOfStock") === "1"
  );
  const [sort, setSort] = useState(() => searchParams.get("sort") || "recent");
  const [page, setPage] = useState(() => {
    const value = Number(searchParams.get("page"));
    return Number.isFinite(value) && value > 0 ? value : 1;
  });

  const previousCategorySlugRef = useRef(categorySlug);

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: LIMIT,
  });
  const [hiddenOutOfStockCount, setHiddenOutOfStockCount] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    async function loadCategories() {
      try {
        const storeController = new Store();
        const result = await storeController.getCategories();

        setCategories(getCategoriesResponse(result));
      } catch (categoriesError) {
        console.error(
          "StoreCatalogPage loadCategories error:",
          categoriesError
        );

        setCategories([]);
      }
    }

    loadCategories();
  }, []);

  useEffect(() => {
    async function loadFilterBounds() {
      try {
        const storeController = new Store();

        const result = await storeController.getFilters({
          category: categorySlug || undefined,
        });

        const bounds = getFiltersResponse(result);

        setFilterBounds(bounds);

        if (previousCategorySlugRef.current !== categorySlug) {
          setSelectedColors([]);
          setSelectedSizes([]);
          setShowAllColors(false);
          setSizeQuery("");
          setShowAllSizes(false);
          setPage(1);
        }

        previousCategorySlugRef.current = categorySlug;
      } catch (boundsError) {
        console.error("StoreCatalogPage loadFilterBounds error:", boundsError);
      }
    }

    loadFilterBounds();
  }, [categorySlug]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);

        if (selectedColors.length > 0) {
          next.set("color", selectedColors.join(","));
        } else {
          next.delete("color");
        }

        if (selectedSizes.length > 0) {
          next.set("size", selectedSizes.join(","));
        } else {
          next.delete("size");
        }

        if (showOutOfStock) {
          next.set("outOfStock", "1");
        } else {
          next.delete("outOfStock");
        }

        if (sort !== "recent") {
          next.set("sort", sort);
        } else {
          next.delete("sort");
        }

        if (page > 1) {
          next.set("page", String(page));
        } else {
          next.delete("page");
        }

        return next;
      },
      { replace: true }
    );
  }, [selectedColors, selectedSizes, showOutOfStock, sort, page, setSearchParams]);

  const loadProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const storeController = new Store();

      const result = await storeController.getProducts(page, LIMIT, {
        category: categorySlug || undefined,
        search: search || undefined,
        color: selectedColors.join(",") || undefined,
        size: selectedSizes.join(",") || undefined,
        availability: showOutOfStock ? undefined : "inStock",
        sort,
      });

      const response = getProductsResponse(result);

      setProducts(response.docs);
      setHiddenOutOfStockCount(response.hiddenOutOfStockCount);

      setPagination({
        totalDocs: response.totalDocs,
        totalPages: response.totalPages,
        page: response.page,
        limit: response.limit,
      });
    } catch (productsError) {
      console.error("StoreCatalogPage loadProducts error:", productsError);

      setProducts([]);
      setHiddenOutOfStockCount(0);

      setError(
        productsError?.msg ||
          "No fue posible cargar el catálogo. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    categorySlug,
    showOutOfStock,
    page,
    search,
    selectedColors,
    selectedSizes,
    sort,
  ]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === categorySlug),
    [categories, categorySlug]
  );

  const hasFilters = Boolean(
    selectedColors.length > 0 || selectedSizes.length > 0 || showOutOfStock
  );

  const activeFilterCount =
    selectedColors.length + selectedSizes.length + (showOutOfStock ? 1 : 0);

  const visibleColorOptions = showAllColors
    ? filterBounds.colors
    : filterBounds.colors.slice(0, COLOR_FILTER_VISIBLE_LIMIT);

  const filteredSizeOptions = useMemo(() => {
    const query = sizeQuery.trim().toLowerCase();

    if (!query) {
      return filterBounds.sizes;
    }

    return filterBounds.sizes.filter((size) =>
      size.toLowerCase().includes(query)
    );
  }, [filterBounds.sizes, sizeQuery]);

  const visibleSizeOptions = showAllSizes
    ? filteredSizeOptions
    : filteredSizeOptions.slice(0, SIZE_FILTER_VISIBLE_LIMIT);

  const activeFilterChips = useMemo(() => {
    const chips = [];

    selectedColors.forEach((colorKey) => {
      const color = filterBounds.colors.find((item) => item.key === colorKey);

      chips.push({
        key: `color-${colorKey}`,
        label: color?.label || colorKey,
        swatch: color?.swatch,
        onRemove: () => {
          setSelectedColors((current) =>
            current.filter((key) => key !== colorKey)
          );
          setPage(1);
        },
      });
    });

    selectedSizes.forEach((size) => {
      chips.push({
        key: `size-${size}`,
        label: size,
        onRemove: () => {
          setSelectedSizes((current) => current.filter((item) => item !== size));
          setPage(1);
        },
      });
    });

    if (showOutOfStock) {
      chips.push({
        key: "outOfStock",
        label: "Incluye agotados",
        onRemove: () => {
          setShowOutOfStock(false);
          setPage(1);
        },
      });
    }

    return chips;
  }, [selectedColors, selectedSizes, showOutOfStock, filterBounds.colors]);

  function handleClearFilters() {
    setSelectedColors([]);
    setSelectedSizes([]);
    setShowOutOfStock(false);
    setPage(1);
  }

  return (
    <div className={styles.StoreCatalogPage}>
      <header className={styles.PageHeader}>
        <p className={styles.Breadcrumb}>
          <Link to="/">Inicio</Link> / Catálogo
          {activeCategory ? ` / ${activeCategory.name}` : ""}
        </p>

        <h1>
          {search
            ? `Resultados para "${search}"`
            : activeCategory?.name || "Catálogo"}
        </h1>
      </header>

      <div className={styles.Layout}>
        <button
          type="button"
          className={styles.MobileFilterToggle}
          onClick={() => setShowMobileFilters((current) => !current)}
          aria-expanded={showMobileFilters}
        >
          <span>
            Filtros
            {activeFilterCount > 0 && (
              <span className={styles.MobileFilterCount}>
                {activeFilterCount}
              </span>
            )}
          </span>

          <span aria-hidden="true">{showMobileFilters ? "▲" : "▼"}</span>
        </button>

        <aside
          className={`${styles.Sidebar} ${
            showMobileFilters ? styles.SidebarOpen : ""
          }`}
        >
          {activeFilterChips.length > 0 && (
            <div className={styles.ActiveFilters}>
              <div className={styles.ActiveFiltersHeader}>
                <span>Filtros activos</span>

                <button
                  type="button"
                  className={styles.ClearButton}
                  onClick={handleClearFilters}
                >
                  Limpiar todo
                </button>
              </div>

              <div className={styles.ActiveFilterChips}>
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    className={styles.ActiveFilterChip}
                    onClick={chip.onRemove}
                  >
                    {chip.swatch && (
                      <span
                        className={styles.ActiveFilterSwatch}
                        style={{ backgroundColor: chip.swatch }}
                        aria-hidden="true"
                      />
                    )}

                    {chip.label}

                    <span aria-hidden="true">✕</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {filterBounds.colors.length > 0 && (
            <FilterSection title="Color" count={selectedColors.length}>
              <div className={styles.ColorChipList}>
                {visibleColorOptions.map((color) => (
                  <button
                    key={color.key}
                    type="button"
                    className={`${styles.ColorChip} ${
                      selectedColors.includes(color.key)
                        ? styles.ColorChipActive
                        : ""
                    }`}
                    style={{ backgroundColor: color.swatch }}
                    title={color.label}
                    aria-label={color.label}
                    aria-pressed={selectedColors.includes(color.key)}
                    onClick={() => {
                      setSelectedColors((current) =>
                        toggleValue(current, color.key)
                      );
                      setPage(1);
                    }}
                  />
                ))}
              </div>

              {filterBounds.colors.length > COLOR_FILTER_VISIBLE_LIMIT && (
                <button
                  type="button"
                  className={styles.ShowMoreButton}
                  onClick={() => setShowAllColors((current) => !current)}
                >
                  {showAllColors
                    ? "Ver menos"
                    : `Ver todos (${filterBounds.colors.length})`}
                </button>
              )}
            </FilterSection>
          )}

          {filterBounds.sizes.length > 0 && (
            <FilterSection title="Talla" count={selectedSizes.length}>
              {filterBounds.sizes.length > SIZE_SEARCH_THRESHOLD && (
                <input
                  type="search"
                  className={styles.FilterSearchInput}
                  placeholder="Buscar talla..."
                  value={sizeQuery}
                  onChange={(event) => setSizeQuery(event.target.value)}
                  aria-label="Buscar talla"
                />
              )}

              {visibleSizeOptions.length === 0 ? (
                <p className={styles.FilterEmptyMessage}>
                  Ninguna talla coincide con "{sizeQuery}".
                </p>
              ) : (
                <div className={styles.OptionList}>
                  {visibleSizeOptions.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={`${styles.OptionChip} ${
                        selectedSizes.includes(size)
                          ? styles.OptionChipActive
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedSizes((current) => toggleValue(current, size));
                        setPage(1);
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}

              {!sizeQuery && filteredSizeOptions.length > SIZE_FILTER_VISIBLE_LIMIT && (
                <button
                  type="button"
                  className={styles.ShowMoreButton}
                  onClick={() => setShowAllSizes((current) => !current)}
                >
                  {showAllSizes
                    ? "Ver menos"
                    : `Ver todas (${filteredSizeOptions.length})`}
                </button>
              )}
            </FilterSection>
          )}

          <div className={styles.FilterGroup}>
            <label className={styles.CheckboxRow}>
              <input
                type="checkbox"
                checked={showOutOfStock}
                onChange={(event) => {
                  setShowOutOfStock(event.target.checked);
                  setPage(1);
                }}
              />

              <span>Mostrar también agotados</span>
            </label>
          </div>

          <button
            type="button"
            className={styles.MobileFilterCloseButton}
            onClick={() => setShowMobileFilters(false)}
          >
            Ver {pagination.totalDocs}{" "}
            {pagination.totalDocs === 1 ? "producto" : "productos"}
          </button>
        </aside>

        <section className={styles.Results}>
          <div className={styles.ResultsHeader}>
            <span className={styles.ResultsCount}>
              {pagination.totalDocs}{" "}
              {pagination.totalDocs === 1 ? "producto" : "productos"}
            </span>

            <label className={styles.SortControl}>
              <span>Ordenar por</span>

              <select
                value={sort}
                onChange={(event) => {
                  setSort(event.target.value);
                  setPage(1);
                }}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && (
            <div className={styles.ErrorMessage} role="alert">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className={styles.StateMessage}>Cargando productos...</div>
          ) : products.length === 0 ? (
            <div className={styles.StateMessage}>
              {hiddenOutOfStockCount > 0 ? (
                <>
                  {hiddenOutOfStockCount === 1
                    ? "Hay 1 producto que coincide con tu búsqueda, pero está agotado."
                    : `Hay ${hiddenOutOfStockCount} productos que coinciden con tu búsqueda, pero están agotados.`}
                  <br />
                  <button
                    type="button"
                    className={styles.InlineLinkButton}
                    onClick={() => {
                      setShowOutOfStock(true);
                      setPage(1);
                    }}
                  >
                    Mostrar también agotados
                  </button>
                </>
              ) : (
                "No se encontraron productos con los filtros seleccionados."
              )}
            </div>
          ) : (
            <>
              <div className={styles.ProductGrid}>
                {products.map((product) => {
                  const productColors = getProductColors(product);
                  const visibleColors = productColors.slice(
                    0,
                    MAX_VISIBLE_COLORS
                  );
                  const extraColorCount =
                    productColors.length - visibleColors.length;

                  return (
                    <Link
                      key={product._id}
                      to={`/producto/${product.slug}`}
                      state={{ from: `${location.pathname}${location.search}` }}
                      className={styles.ProductCard}
                    >
                      <span className={styles.ProductImageWrapper}>
                        {product.image ? (
                          <>
                            <span
                              className={styles.ProductImageBackdrop}
                              style={{
                                backgroundImage: `url(${getImageUrl(
                                  product.image
                                )})`,
                              }}
                              aria-hidden="true"
                            />

                            <img
                              src={getImageUrl(product.image)}
                              alt={product.name}
                              className={styles.ProductImage}
                            />
                          </>
                        ) : (
                          <span className={styles.ProductImagePlaceholder}>
                            ▣
                          </span>
                        )}

                        {!product.inStock && (
                          <span className={styles.OutOfStockBadge}>
                            Agotado
                          </span>
                        )}
                      </span>

                      {product.brand && (
                        <span className={styles.ProductBrand}>
                          {product.brand}
                        </span>
                      )}

                      <span className={styles.ProductName}>{product.name}</span>

                      {visibleColors.length > 0 && (
                        <span
                          className={styles.ProductColorList}
                          aria-label={`Colores disponibles: ${productColors
                            .map((color) => color.label)
                            .join(", ")}`}
                        >
                          {visibleColors.map((color) => (
                            <span
                              key={color.value}
                              className={styles.ProductColorSwatch}
                              style={{ backgroundColor: color.value }}
                              title={color.label}
                            />
                          ))}

                          {extraColorCount > 0 && (
                            <span className={styles.ProductColorExtra}>
                              +{extraColorCount}
                            </span>
                          )}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>

              {pagination.totalPages > 1 && (
                <footer className={styles.Pagination}>
                  <button
                    type="button"
                    className={styles.PageButton}
                    onClick={() => setPage((current) => Math.max(current - 1, 1))}
                    disabled={pagination.page === 1}
                  >
                    ‹ Anterior
                  </button>

                  <span className={styles.CurrentPage}>
                    {pagination.page} de {pagination.totalPages}
                  </span>

                  <button
                    type="button"
                    className={styles.PageButton}
                    onClick={() =>
                      setPage((current) =>
                        Math.min(current + 1, pagination.totalPages)
                      )
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Siguiente ›
                  </button>
                </footer>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
